import { Box, Flex, Popover, Text } from '@mantine/core'
import { useEffect, useState } from 'react'

import { useTranslation } from '@flecto/translations'
import {
  type ParameterID,
  type CoreProduct,
  type CoreBundleItem,
  type CoreProductSelection,
} from '@flecto/types'

import { AvatarItem, BadgeProductInsurance } from '..'
import { useProductSelectionStore } from '../../../flecto-modules-saas/src/bookings/hooks/useProductSelectionStore'
import { usePopoverTrackedState } from '../PopoverTracked/usePopoverTrackedState'
import classes from './styles.module.css'

export type PopoverBookingProductBundleItems = {
  product: CoreProduct
  qtdSelected?: number
  withBadgeInsurance?: boolean
  onItemClick?: (itemId: ParameterID) => void
  itemsSelectionMap?: Map<ParameterID, CoreProductSelection>
  withStock?: boolean
  children?: React.ReactNode
}

export function PopoverBookingProductBundleItems({
  product,
  children,
  qtdSelected,
  onItemClick,
  itemsSelectionMap: propItemsSelectionMap,
  withBadgeInsurance = true,
  withStock,
}: PopoverBookingProductBundleItems) {
  const { t } = useTranslation('shared', { keyPrefix: 'shared' })
  const itemsSelectionMapFromStore = useProductSelectionStore(
    (state) => state.itemsSelectionMap
  )
  const { popoverIdOpened, setPopoverIdOpened, isNonExclusive } =
    usePopoverTrackedState()

  const popoverId = `popover-booking-bundle-${product?.id}`
  const [localOpened, setLocalOpened] = useState(false)

  const opened = isNonExclusive()
    ? localOpened
    : localOpened && popoverIdOpened === popoverId

  useEffect(() => {
    if (
      !isNonExclusive() &&
      popoverIdOpened &&
      popoverIdOpened !== popoverId &&
      localOpened
    ) {
      setLocalOpened(false)
    }
  }, [popoverIdOpened, popoverId, localOpened])

  const finalItemsSelectionMap =
    propItemsSelectionMap ?? itemsSelectionMapFromStore
  if (!product?.bundle) {
    return children
  }
  const inAvailabilityMode = typeof qtdSelected === 'number'
  const inStockMode = withStock && !inAvailabilityMode
  const shouldShowInsuranceBadge = withBadgeInsurance && product.insured

  const handleItemClick = (itemId: ParameterID) => {
    if (onItemClick) {
      onItemClick(itemId)
    } else {
      window.open(`/products/item/${itemId}`, '_blank')
    }
    setLocalOpened(false)
    if (!isNonExclusive()) {
      setPopoverIdOpened(null)
    }
  }

  const renderQuantity = (bi: CoreBundleItem) => {
    const item = bi.item
    const track = item.track_quantity

    const isUnlimited = !track
    const itemSelection = finalItemsSelectionMap?.get(item.id)
    const fallbackQuantity =
      item.quantity_available ?? item.item_attributes?.quantity_available ?? 0

    const quantityToDisplay =
      itemSelection?.remaining_allocated_quantity ?? fallbackQuantity

    return (
      <>
        <Text size='md' className={classes.quantity} ta='right'>
          x{bi.quantity}
        </Text>

        {inStockMode && (
          <Text size='sm' c='dimmed' className={classes.availability}>
            {isUnlimited
              ? t('new_bundle_items_unlimited')
              : t('new_bundle_items_availability', {
                  count: quantityToDisplay,
                  replace: { count: quantityToDisplay },
                })}
          </Text>
        )}

        {inAvailabilityMode && !inStockMode && (
          <Text size='sm' c='dimmed' className={classes.availability}>
            {isUnlimited
              ? t('new_bundle_items_unlimited')
              : `${quantityToDisplay} ${t('in_stock')}`}
          </Text>
        )}
      </>
    )
  }

  const renderBundleItems = () => {
    return product.bundle_attributes?.bundle_items?.map((bi) => (
      <Box
        key={bi.id}
        className={classes.item}
        onClick={() => handleItemClick(bi.item.internal_id)}
        data-testid='popover-item'
      >
        <AvatarItem item={bi.item} className={classes.avatar} />
        <Flex className={classes.title}>
          <Text fw={500}>{bi.item.title}</Text>
          {shouldShowInsuranceBadge && (
            <BadgeProductInsurance
              data-testid='badge-product-insurance'
              product={product}
            />
          )}
        </Flex>
        <Text size='sm' c='dimmed' className={classes.id}>
          ID: {bi.item.internal_id}
        </Text>
        {renderQuantity(bi)}
      </Box>
    ))
  }

  const handleClick = () => {
    const newOpened = !opened
    setLocalOpened(newOpened)
    if (!isNonExclusive()) {
      if (newOpened) {
        setPopoverIdOpened(popoverId)
      } else {
        setPopoverIdOpened(null)
      }
    }
  }

  const handleClose = () => {
    setLocalOpened(false)
    if (!isNonExclusive()) {
      setPopoverIdOpened(null)
    }
  }

  return (
    <Popover
      position='bottom'
      width={330}
      withinPortal
      opened={opened}
      onChange={handleClose}
      closeOnClickOutside
    >
      <Popover.Target>
        <Box
          data-testid='popover-target'
          className={classes.target}
          onClick={(e) => {
            e.stopPropagation()
            handleClick()
          }}
        >
          {children}
        </Box>
      </Popover.Target>
      <Popover.Dropdown data-testid='popover-dropdown'>
        {renderBundleItems()}
      </Popover.Dropdown>
    </Popover>
  )
}
